import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, NavigationHeader } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import InfoCardWithIndex from 'design-system/InfoCardWithIndex';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Amplitude from 'utils/amplitude';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { InvestFunnelEvents } from 'features/funnel-events/types';
import useColors from 'hooks/useColors';
import { View, StyleSheet } from 'react-native';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import SetupInvestmentAccountButton from '../components/SetupInvestmentAccountButton';
import { selectActiveTradingAccountId } from '../reducers/selectors';
const AutoinvestIntroScreen = ({ route, navigation }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const initialAllocations = route.params?.initialAllocations;
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const scrollY = useSharedValue(0);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('How Direct Debit Works', { source: 'invest' });
        }
    }, [mountedOnAnotherScreen]);
    const accountType = route.params?.accountType;
    // Same check AutoinvestPickCollectionDateScreen uses to continue onboarding
    const isOnboarding = useAppSelector((state) => !selectActiveTradingAccountId(state) && !!state.invest.onboarding);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const handleContinue = useCallback(() => {
        if (accountType) {
            if (isOnboarding) {
                dispatch(postFunnelEvents([InvestFunnelEvents.ONB_INVEST_NEXT_COLLECTION_DATE]));
            }
            navigation.navigate('AutoinvestPickCollectionDate', {
                accountType,
                initialAllocations,
            });
        }
    }, [accountType, initialAllocations, isOnboarding]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y > 0 ? contentOffset.y : 0;
    });
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="How Autoinvest works:" preTitle="Let’s get started"/>
        <InfoCardWithIndex index={1} title="Set your schedule & amount" subTitle="Choose the day of the month you want to invest and how much you’d like to deposit."/>
        <InfoCardWithIndex index={2} title="Build your portfolio" subTitle="Choose how your money is allocated across funds and update it whenever you like."/>
        <InfoCardWithIndex index={3} title="Invest automatically" subTitle="Your money is collected on your chosen date every month. No manual orders needed."/>
      </Animated.ScrollView>
      <ButtonGroup type="floating" isInTab={!!mountedOnAnotherScreen}>
        {accountType ? (<Button brand title="Continue" onPress={handleContinue}/>) : (<SetupInvestmentAccountButton brand/>)}
      </ButtonGroup>
    </AppView>);
};
const styles = StyleSheet.create({
    rightBtnContainer: {
        paddingRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
});
export default AutoinvestIntroScreen;
